import React, { useState, Suspense, useEffect } from "react";
import { CoverageFinderService, CommonService } from "./services";
import "./app/coveragedemo.css";
import Navbar from "./components/Navbar";
import DemoShell from "./components/DemoShell";

const Home = () => {
  const [token, setToken] = useState("");
  const [indication, setIndication] = useState("");
  const [cssClassName, setCSSClassName] = useState("hcpContent");
  const [mainHeaderText, setMainHeaderText] = useState("See Formulary Coverage in Your Area");
  const [topNavbarTexts, setTopNavbarTexts] = useState([
    "For US Healthcare Professionals Only",
    "Important Safety Information",
    "Prescribing Information",
    "For Patients",
  ]);
  const [bottomNavbarTexts, setBottomNavbarTexts] = useState([
    "About",
    "Norstella",
    "Efficacy",
    "Safety",
    "Access",
    "Resources",
  ]);
  const [showNorstellaBranding, setShowNorstellaBranding] = useState(false);
  const [coverageFinderResponse, setCoverageFinderResponse] = useState(null);
  const [dynamicStyle, setDynamicStyle] = useState();
  
  // Replace useSearchParams with URLSearchParams
  const queryParameters = new URLSearchParams(window.location.search);
  const tokenParam = queryParameters.get("token");
  const pfTokenParam = queryParameters.get("PFtoken");
  const indicationId = queryParameters.get("indication");
  const zipCodeId = queryParameters.get("zipcode");
  const loadingIcon = queryParameters.get("loadingIcon") ?? "";
  const trackEvents = queryParameters.get("trackEvents")?.toLowerCase() === "true" ? true : false;
  const validatePositiveIntegerParam = (value) => (Number.isInteger(Number(value)) && Number(value) > 0 ? value : null);
  const productIdParam = validatePositiveIntegerParam(queryParameters.get("productId"));
  const benefitTypeIdParam = validatePositiveIntegerParam(queryParameters.get("benefitTypeId"));
  let version = queryParameters.get("version") ?? "";
  let demoId = tokenParam ? tokenParam.toLowerCase() : process.env.REACT_APP_DEMOID;
  let pfDemoId = pfTokenParam ? pfTokenParam.toLowerCase() : process.env.REACT_APP_PFDEMOID;
  version = version ? "-" + version : "";
  
  const isPatientFinder = () => {
    return pfDemoId;
  };
  
  useEffect(() => {
    if (trackEvents) {
      const coverageFinder = document.getElementById('coverageFinder');
      if (coverageFinder) {
        const handleTrackEvent = (event) => {
          const componentType = event?.detail?.componentType ?? '';
          const eventName = event?.detail?.eventName ?? '';
          const eventDetail = event?.detail?.eventDetail ?? {};
          const timestamp = event?.detail?.timestamp ?? '';
          console.log(timestamp, '|', componentType, '|', eventName, ':', JSON.stringify(eventDetail ?? {}));
        };
        coverageFinder.addEventListener('coverage-finder-track-event', handleTrackEvent);
        
        // Cleanup on unmount
        return () => {
          coverageFinder.removeEventListener('coverage-finder-track-event', handleTrackEvent);
        };
      }
    }
  }, [trackEvents, token]);
  
  const updateDynamicStyle = (clientId, uiContainerSize, uiContainerHeight, mobileBreakpoint) => {
    const viewportWidth = Math.min(window.innerWidth, document.documentElement.clientWidth);
    const currentIsDesktop = viewportWidth > mobileBreakpoint;
    const matchesOrExceedsContainerSize = viewportWidth >= uiContainerSize;
    let defaultWidth = 1200;
    let width;
    let maxWidth;
    
    if (uiContainerSize > 0 && currentIsDesktop) {
      
      if (matchesOrExceedsContainerSize) {
        width = `${uiContainerSize}px`;
        maxWidth = `${uiContainerSize}px`;
      }
      else {
        width = "auto";
        maxWidth = `${uiContainerSize}px`;
      }
    } else if (clientId === 10) {
      if (isPatientFinder()) {
        if (viewportWidth >= defaultWidth) {
          width = currentIsDesktop ? "1200px" : "auto";
          maxWidth = currentIsDesktop ? "1200px" : "auto";
        }
        else {
          width = "auto";
        }
      }
      else {
        if (viewportWidth >= defaultWidth) {
          width = "1200px";
          maxWidth = "1200px";
        }
        else {
          maxWidth = currentIsDesktop ? viewportWidth : "calc(100% - 20px)";
        }
      }
    } else {
      // Default fallback when no specific width is set
      width = "auto";
    }

    let height;

    if (uiContainerHeight > 0 && currentIsDesktop) {
      height = `${ uiContainerHeight + 60 }px`; //Add 60 to account for top and bottom padding
    }
    else {
      height = "auto";
    }

   if (isPatientFinder()) {
      setDynamicStyle({ maxWidth, width, height });
    } else {
      setDynamicStyle({ maxWidth, width });
    }

    applyMediaQueryStyles(mobileBreakpoint);
  };

  const applyMediaQueryStyles = (mobileBreakpoint) => {
      const css = `
        @media (min-width: ${mobileBreakpoint + 1}px) {
            .pf-padding {
              padding-left: 30px !important;
              padding-right: 30px !important;
              padding-top: 30px !important;
              padding-bottom: 30px !important;
            }

            .inner-content {
              margin-left: auto;
              margin-right: auto;
            }
        }
        @media (max-width: ${mobileBreakpoint}px) {
            .norstella-header {
              display: flex;
              flex-direction: column;
            }

            header {
              padding: 0 20px;
            }

            .topnav {
              margin-top: 10px;
            }

            nav {
              margin-top: 20px;
            }

            .heading {
              margin-left: 10px;
              margin-right: 10px;
            }
        }
      `;

      const style = document.createElement('style');
      style.textContent = css;
      document.head.appendChild(style);
  };

  async function getClientCoverageInfo(demoId, projectType) {
    const coverageInfoResponse = await CoverageFinderService.getInstance().getCoverageFinderInfo(demoId, projectType);
    if (coverageInfoResponse != null && coverageInfoResponse.token) {
      setCoverageFinderResponse(coverageInfoResponse);
      setToken(coverageInfoResponse.token);
      if (indicationId?.toLowerCase() === "none") {
        setIndication(null);
      }
      else {
        setIndication(indicationId ?? coverageInfoResponse.indicationId);
      }
      const jwtWebsiteType = CommonService.parseJwt(coverageInfoResponse.token)?.websiteType ?? 1;
      if (jwtWebsiteType == 2 || jwtWebsiteType == 4) {
        document.title = "Patient Finder Demo";
        setCSSClassName("patientContent");
        setMainHeaderText("See Insurance Coverage in Your Area");
        setTopNavbarTexts([
          "Patient Information",
          "Important Safety Information",
          "Instructions for Use",
          "For Healthcare Professionals",
        ]);
        setBottomNavbarTexts([
          "About Norstella",
          "Results on Norstella",
          "How to use Norstella",
          "Savings & Support Programs",
          "Norstella Resources",
        ]);
      } else {
        document.title = "Coverage Finder Demo";
      }
      if (coverageInfoResponse.clientId === 10) {
        //Show Norstella branding only for Sunny client
        setShowNorstellaBranding(true);
        setCSSClassName((prevClassName) => `${prevClassName} sunnyClient`);
        document.body.style.background = "#001E43";
      }
      const uiContainerSize = coverageInfoResponse?.uiContainerSize ?? 0;
      const uiContainerHeight = coverageInfoResponse?.uiContainerHeight ?? 0;
      const mobileBreakpoint =  coverageInfoResponse?.mobileBreakpoint && coverageInfoResponse?.mobileBreakpoint > 0 ? coverageInfoResponse.mobileBreakpoint : 1032;
      updateDynamicStyle(coverageInfoResponse.clientId, uiContainerSize, uiContainerHeight, mobileBreakpoint);
    }
  }
  
  useEffect(() => {
    if (coverageFinderResponse == null) {
      if (pfDemoId) {
        getClientCoverageInfo(pfDemoId, "PF");
      } else {
        getClientCoverageInfo(demoId, "CF");
      }
    }
  }, [coverageFinderResponse, pfDemoId, demoId]);
  
  useEffect(() => {
    const handleResize = () => {
      const uiContainerSize = coverageFinderResponse?.uiContainerSize ?? 0;
      const uiContainerHeight = coverageFinderResponse?.uiContainerHeight ?? 0;
      const mobileBreakpoint = coverageFinderResponse?.mobileBreakpoint && coverageFinderResponse?.mobileBreakpoint > 0 ? coverageFinderResponse.mobileBreakpoint : 1032;
      updateDynamicStyle(coverageFinderResponse?.clientId, uiContainerSize, uiContainerHeight, mobileBreakpoint);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [coverageFinderResponse]);
  
  useEffect(() => {
    import("@norstella/coverage-finder" + version + "/dist/coverage-finder.js").catch((error) => {
      console.error(error);
    });
  }, [version]);

  return (
    <>
      {!token && <div style={{textAlign: 'center', padding: '50px'}}>Loading...</div>}
      {showNorstellaBranding && token && (
        <>
          <header className="demo-header">
            <div className="norstella-header">
              <div>
                <a href="https://www.norstella.com/" rel="home" aria-current="page">
                  <svg id="Layer_1" width="250" height="86" viewBox="0 0 417 144">
                    <g>
                      <path
                        id="st0"
                        className="st0"
                        d="M381.08,54.06h2.77v0.64h-0.97v3.38h-0.82l0.01-3.38h-0.98V54.06z M389.08,54.06v4.02h-0.77l-0.01-2.91 l-1.25,2.91h-0.58l-1.23-2.96v2.96h-0.75v-4.02h1.06l1.25,3.05l1.28-3.05H389.08z"
                      ></path>
                      <path
                        id="st0"
                        className="st0"
                        d="M159.74,85.88c0,2.14-1.63,3.69-3.72,3.69c-2.09,0-3.72-1.54-3.72-3.69V71.44c-0.11-6.53-3.43-9.93-8.05-9.93 c-5.91,0-8.76,5.19-8.76,9.74v14.52c0,2.12-1.61,3.72-3.72,3.72c-2.13,0-3.76-1.7-3.76-3.72V57.78c0-2.15,1.83-3.76,3.8-3.76 c1.95,0,3.69,1.35,3.69,4.29c4.2-4.04,8.16-4.25,9.59-4.25c4.97,0,8.47,2.38,10.61,4.95c3.12,3.76,4.06,8.54,4.06,12.43V85.88z M181.44,54.06c9.93,0,17.9,8.11,17.9,17.94c0,9.79-7.93,17.94-17.9,17.94c-9.84,0-17.94-8.02-17.94-17.94 C163.5,62.08,171.6,54.06,181.44,54.06z M181.44,82.42c5.71,0,10.42-4.6,10.42-10.42c0-5.78-4.61-10.38-10.42-10.38 c-5.8,0-10.42,4.69-10.42,10.38C171.02,77.71,175.72,82.42,181.44,82.42z M211.19,57.86c3.18-2.43,6.98-3.8,11.21-3.8 c3.38,0,4.02,2.37,4.02,3.72c0,2.22-1.76,3.72-3.87,3.72c-0.5,0-4.64-0.16-8.07,3.31c-2.01,2.03-3.29,4.86-3.29,8.05v12.9 c0,2.12-1.61,3.72-3.72,3.72c-2.13,0-3.76-1.7-3.76-3.72V57.78c0-2.08,1.72-3.76,3.8-3.76C210.74,54.02,211.19,57.19,211.19,57.86z M235.77,73.22c-7.09-3.07-7.47-7-7.47-8.93c0-6.35,6-10.34,12.37-10.34c4.54,0,9.37,1.9,10.94,5.07c1.03,2.15-0.21,3.48-0.41,3.69 c-0.86,0.96-2.33,1.49-3.69,0.87c-2.3-1.64-3.72-3.01-6.32-3.01c-2.62,0-5.38,0.88-5.38,3.54c0,3.42,5.34,3.95,10.68,6.13 c3.52,1.43,7,3.97,7,9.03c0,6.42-5.96,10.64-12.64,10.64c-4.53,0-10.39-1.72-12.86-6.39c-0.9-1.64-0.6-2.96-0.13-3.72 c1.11-1.83,4.21-2.56,6.38,0.11c1.97,2.9,4.24,3.38,6.58,3.38c2.69,0,5.42-1.34,5.42-3.76C246.24,75.98,241.82,75.85,235.77,73.22z M269.67,54.13h3.23c1.16,0,3.76,0.76,3.76,3.69c0,2.16-1.58,3.8-3.76,3.8h-3.23V85.8c0,2.13-1.62,3.69-3.72,3.69 c-2.07,0-3.76-1.52-3.76-3.69V61.62h-2.71c-2.15,0-3.76-1.84-3.76-3.8c0-2.16,1.69-3.69,3.76-3.69h2.71v-9.1 c0-2.16,1.69-3.69,3.76-3.69c2.09,0,3.72,1.54,3.72,3.69V54.13z M285.83,75.38c1.23,4.31,5.12,7.33,9.7,7.33 c1.91,0,4.6-0.32,6.73-2.67c2.44-1.88,4.45-0.4,4.78-0.09c0.59,0.56,2.88,3.47-0.04,5.89c-2.82,2.7-6.54,4.06-11.47,4.06 c-10.06,0-18.02-8.11-18.02-17.9c0-9.91,8.12-17.94,18.02-17.94c9.87,0,17.92,8.02,18.01,17.75c0,2.92-2.52,3.57-3.69,3.57H285.83z M305.84,69.14c-1.16-4.85-5.39-7.94-10.19-7.94c-4.96,0-9.02,3.32-10.04,7.94H305.84z M326.04,85.8c0,2.15-1.64,3.69-3.72,3.69 c-2.08,0-3.76-1.52-3.76-3.69V45.03c0-2.16,1.68-3.69,3.76-3.69c2.08,0,3.72,1.54,3.72,3.69V85.8z M340.7,85.8 c0,2.15-1.64,3.69-3.72,3.69c-2.08,0-3.76-1.52-3.76-3.69V45.03c0-2.16,1.68-3.69,3.76-3.69c2.08,0,3.72,1.54,3.72,3.69V85.8z M345.24,72c0-9.92,8.1-17.94,17.94-17.94c10.01,0,17.9,8.25,17.9,17.94v13.65c0,1.82-1.41,3.84-3.84,3.84 c-2.43,0-3.5-2.02-3.65-3.42c-2.35,1.91-5.89,3.87-10.42,3.87C353.34,89.94,345.24,81.92,345.24,72z M352.76,72 c0,5.71,4.7,10.42,10.42,10.42c5.71,0,10.42-4.6,10.42-10.42c0-5.78-4.61-10.38-10.42-10.38C357.37,61.62,352.76,66.32,352.76,72z"
                      ></path>
                      <path
                        id="st1"
                        className="st1"
                        d="M108,72c0,1.72-1.39,3.11-3.11,3.11s-3.11-1.39-3.11-3.11c0-15.37-11.7-28.05-26.66-29.61v6.54 c5.04,0.67,9.7,2.96,13.36,6.62c1.21,1.22,1.21,3.19,0,4.4s-3.19,1.21-4.4,0c-3.22-3.23-7.51-5-12.07-5 c-9.4,0-17.06,7.65-17.06,17.05c0,1.72-1.39,3.11-3.11,3.11s-3.11-1.39-3.11-3.11c0-11.78,8.8-21.54,20.17-23.07v-6.55 c-6.78,0.7-13.06,3.67-17.94,8.56c-1.22,1.22-3.19,1.22-4.4,0c-1.22-1.22-1.22-3.19,0-4.4C53.34,39.75,62.38,36,72,36 C91.85,36,108,52.15,108,72z M97.46,93.05c-1.22-1.22-3.19-1.22-4.4,0c-4.88,4.88-11.17,7.86-17.94,8.56v-6.55 C86.48,93.54,95.28,83.78,95.28,72c0-1.72-1.39-3.11-3.11-3.11c-1.72,0-3.11,1.39-3.11,3.11c0,9.4-7.65,17.05-17.06,17.05 c-4.56,0-8.84-1.78-12.07-5c-1.21-1.22-3.19-1.22-4.4,0c-1.22,1.21-1.22,3.19,0,4.4c3.66,3.66,8.31,5.95,13.36,6.62v6.54 C53.93,100.05,42.22,87.37,42.22,72c0-1.72-1.39-3.11-3.11-3.11C37.39,68.89,36,70.28,36,72c0,19.85,16.15,36,36,36 c9.62,0,18.66-3.75,25.46-10.55C98.67,96.24,98.67,94.27,97.46,93.05z M82.16,78.92v-8.18c0-5.6-4.56-10.16-10.16-10.16 s-10.16,4.56-10.16,10.16v8.18c0,1.72,1.39,3.11,3.11,3.11s3.11-1.39,3.11-3.11v-8.18c0-2.17,1.77-3.94,3.94-3.94 s3.94,1.77,3.94,3.94v8.18c0,1.72,1.39,3.11,3.11,3.11S82.16,80.64,82.16,78.92z"
                      ></path>
                    </g>
                  </svg>
                </a>
              </div>
              <Navbar
                items={topNavbarTexts}
                className="topnav"
                separatorClassName="topnavseparator"
                itemClassName="topnavitem"
              />
            </div>
            <nav>
              <Navbar
                items={bottomNavbarTexts}
                className="nav"
                separatorClassName="navseparator"
                itemClassName="navitem"
              />
            </nav>
          </header>

          <div className={cssClassName}>
            <h2 role="heading" aria-level="1" className="heading heading-bold">
              {mainHeaderText}
            </h2>
            <div className={`inner-content ${isPatientFinder() ? 'pf-padding' : 'cf-padding'}`} style={dynamicStyle}>
              <coverage-finder
                  id="coverageFinder"
                  class="coverageFinder"
                  zipCode={zipCodeId}
                  indication={indication}
                  environment={process.env.REACT_APP_ENVIRONMENT}
                  token={token}
                  loadingIcon={loadingIcon}
                  productid={productIdParam}
                  benefittypeid={benefitTypeIdParam}
                />
              </div>
          </div>
        </>
      )}
      {!showNorstellaBranding && token && (
        <div className="content no-branding-content">
          <div className={`inner-content no-branding-center ${isPatientFinder() ? 'pf-padding' : 'cf-padding'}`} style={dynamicStyle}>
            <coverage-finder
                id="coverageFinder"
                class="coverageFinder"
                zipCode={zipCodeId}
                indication={indication}
                environment={process.env.REACT_APP_ENVIRONMENT}
                token={token}
                loadingIcon={loadingIcon}
                productid={productIdParam}
                benefittypeid={benefitTypeIdParam}
              />
          </div>
        </div>
      )}
    </>
  );
};

const Loading = () => {
  return <div>Loading...</div>;
};

const App = () => {
  return (
    <Suspense fallback={<Loading />}>
      <Home />
      <DemoShell />
    </Suspense>
  );
};

export default App;
